import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LanguageDetails,
  languageDetails,
} from '../../../constants/languageDetails';
import { SVGIconType } from '../../../constants/svgIcon';
import { WebPageType } from '../../../constants/webPageType';
import { TtsLocale } from '../../../models/Base';
import { CircleCheckbox, SearchBar, SvgIcon } from '../../Molecules';
import { BasicPopupContainer } from '../Containers/BasicPopupContainer';

import styles from './QuestionAnswerLanguageSelectPopup.module.css';

type QuestionAnswerLanguageSelectPopupProps = {
  title?: string;
  titleIcon?: SVGIconType;
  languageList: TtsLocale[] | undefined;
  selectedLanguage: TtsLocale | undefined;
  recentLanguage: TtsLocale | undefined;
  onSelectLanguage: (language: TtsLocale) => void;
  onClickOK: () => void;
  onCancel: () => void;
};

export const QuestionAnswerLanguageSelectPopup: React.FC<
  QuestionAnswerLanguageSelectPopupProps
> = ({
  title,
  titleIcon,
  languageList,
  selectedLanguage,
  recentLanguage,
  onSelectLanguage,
  onClickOK,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const languageLists = languageList
    ? (Object.fromEntries(
        Object.entries(languageDetails).filter(([locale]) => {
          return languageList.includes(locale as TtsLocale);
        }),
      ) as Record<TtsLocale, LanguageDetails>)
    : languageDetails;

  const filteredLanguages = Object.entries(languageLists)
    .filter(
      ([, { displayName, nativeName }]) =>
        displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        nativeName.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((l, r) => {
      if (l[0] === (recentLanguage as string)) {
        return -1;
      }
      if (r[0] === (recentLanguage as string)) {
        return 1;
      }
      return 0;
    });

  return (
    <BasicPopupContainer
      onPrimaryButtonClick={onClickOK}
      onSecondaryButtonClick={onCancel}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <SvgIcon
            name={titleIcon ?? SVGIconType.LanguageA}
            width={28}
            height={28}
            color={'black'}
          />
          <span className={styles.titleHeader}>
            {t(title ?? 'Content Language')}
          </span>
        </div>
        <hr />

        <div className={styles.searchBar}>
          <SearchBar
            placeholder={t('Search')}
            value={searchQuery}
            webPageType={WebPageType.JOYAJOY}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSearch={() => {}}
          />
        </div>

        <hr />
        <ul className={styles.languageList}>
          {filteredLanguages.length === 0 ? (
            <li className={styles.notFound}>{t('Not Found')}</li>
          ) : (
            filteredLanguages.map(
              ([key, { displayName, nativeName }], index) => (
                <Fragment key={key}>
                  <li
                    className={styles.languageItem}
                    onClick={() => onSelectLanguage(key as TtsLocale)}
                  >
                    <div className={styles.languageNames}>
                      {recentLanguage === (key as TtsLocale) && (
                        <span className={styles.displayName}>
                          {t('Recent')}
                        </span>
                      )}
                      <span className={styles.nativeName}>{nativeName}</span>
                      <span className={styles.displayName}>
                        {t(displayName)}
                      </span>
                    </div>
                    <CircleCheckbox
                      webPageType={WebPageType.JOYAJOY}
                      checked={selectedLanguage === (key as TtsLocale)}
                    />
                  </li>
                  {index < filteredLanguages.length - 1 && <hr />}
                </Fragment>
              ),
            )
          )}
        </ul>
      </div>
    </BasicPopupContainer>
  );
};
